import { useState, useEffect, createContext, useContext } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Controlla se c'è un token salvato al caricamento
    const token = localStorage.getItem('access_token');
    if (token) {
      checkAuthStatus();
    } else {
      setIsLoading(false);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      console.log('🔍 Checking auth status...'); // 🔥 DEBUG
      const userData = await authService.getCurrentUser();
      console.log('👤 User data from /auth/me:', userData); // 🔥 DEBUG
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('❌ Auth check failed:', error); // 🔥 DEBUG
      // Token non valido, rimuovi dal localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      console.log('🔐 Login response:', response); // 🔥 DEBUG
      
      const { access_token, refresh_token, user: userData } = response;
      
      // Salva i token
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // 🔥 IMPORTANTE: Salva anche i dati utente (inclusi permessi)
      if (userData) {
        console.log('👤 User data:', userData); // 🔥 DEBUG
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        // Fallback: carica i dati utente da /auth/me
        console.warn('⚠️ User data non presente nel login, carico da /auth/me');
        await checkAuthStatus();
      }
      
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error); // 🔥 DEBUG
      if (error?.response?.status === 429) {
        return {
          success: false,
          silent: true,
        };
      }
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Errore durante il login' 
      };
    }
  };

  const logout = () => {
    // Rimuovi i token
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // Resetta lo stato
    setUser(null);
    setIsAuthenticated(false);
  };

  const register = async (payload) => {
    try {
      const response = await authService.register(payload);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error };
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato all\'interno di AuthProvider');
  }
  return context;
}
